import { format } from 'date-fns';

/**
 * Format date according to user preferences
 * @param {string|Date} dateString - The date to format
 * @param {Object} userPreferences - User's display preferences
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateString, userPreferences = {}, includeTime = true) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const dateFormat = userPreferences?.display?.dateFormat || 'MM/DD/YYYY';
  const timeFormat = userPreferences?.display?.timeFormat || '12h';
  
  let formatString = '';
  
  // Date format mapping
  switch (dateFormat) {
    case 'DD/MM/YYYY':
      formatString = 'dd/MM/yyyy';
      break;
    case 'YYYY-MM-DD':
      formatString = 'yyyy-MM-dd';
      break;
    case 'MM/DD/YYYY':
    default:
      formatString = 'MM/dd/yyyy';
      break;
  }
  
  // Add time format if requested
  if (includeTime) {
    const timeFormatString = timeFormat === '24h' ? 'HH:mm:ss' : 'h:mm:ss a';
    formatString += ` ${timeFormatString}`;
  }
  
  try {
    return format(date, formatString);
  } catch (error) {
    console.error('Date formatting error:', error);
    return date.toLocaleString();
  }
};

/**
 * Format date only (no time)
 * @param {string|Date} dateString - The date to format
 * @param {Object} userPreferences - User's display preferences
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, userPreferences = {}) => {
  return formatDateTime(dateString, userPreferences, false);
};

/**
 * Format time only
 * @param {string|Date} dateString - The date to format
 * @param {Object} userPreferences - User's display preferences
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString, userPreferences = {}) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Time';
  
  const timeFormat = userPreferences?.display?.timeFormat || '12h';
  const timeFormatString = timeFormat === '24h' ? 'HH:mm:ss' : 'h:mm:ss a';
  
  try {
    return format(date, timeFormatString);
  } catch (error) {
    console.error('Time formatting error:', error);
    return date.toLocaleTimeString();
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} dateString - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const now = new Date();
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
};
